import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delayMs` of quiet.
 * Handy for search-as-you-type over a large client-side list, so the filter work
 * (and any downstream re-render) coalesces instead of firing on every keystroke.
 */
export function useDebouncedValue<T>(value: T, delayMs = 200): T {
  const isEmpty = (value as unknown) === '';
  const [debounced, setDebounced] = useState(value);

  // An emptied value takes effect at once (a "Clear"/reset action must un-narrow the list immediately,
  // not lag a debounce window behind the now-empty search box). Flush the internal state *synchronously*
  // during render — not just the return value — so that if the user clears and then types again before
  // the timer fires, the next debounce window starts from the cleared state, never the stale pre-clear
  // value. (React's "adjust state during render" pattern; the guard makes it a no-op once flushed.)
  if (isEmpty && debounced !== value) {
    setDebounced(value);
  }

  useEffect(() => {
    if (isEmpty) return; // already flushed synchronously above
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs, isEmpty]);

  return debounced;
}
